import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import InputAdornment from '@mui/material/InputAdornment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationIcon from '@mui/icons-material/LocationOn';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { useWizard } from '../../hooks/WizardProvider';
import useOnboardingSx from '../../hooks/useOnboardingSx';
import StepPanel from '../../components/StepPanel';
import WizardFooter from '../../components/WizardFooter';
import Field from '../../components/ui/Field';
import FormSection from '../../components/ui/FormSection';

export default function Creation() {
  const sx = useOnboardingSx();
  const { draft, setDraft, steps, stepErrors, currentStep, organisationId } = useWizard();
  const account = draft.account;
  const createdOrgId = organisationId || draft.organisation.organisation_id;

  const updateAccount = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      account: { ...prev.account, [field]: value },
    }));
  };

  const addContact = () => {
    const newContact = {
      id: `contact-${Date.now()}`,
      name: '',
      email: '',
      phone: '',
      designation: '',
      department: '',
      singlePointOfContact: '',
    };
    updateAccount('contacts', [...account.contacts, newContact]);
  };

  const updateContact = (index, field, value) => {
    const updatedContacts = [...account.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    updateAccount('contacts', updatedContacts);
  };

  const removeContact = (index) => {
    const updatedContacts = account.contacts.filter((_, i) => i !== index);
    updateAccount('contacts', updatedContacts);
  };

  if (!createdOrgId) {
    return (
      <>
        <StepPanel stepId={2} stepStatus={steps.account}>
          <Alert severity="warning">
            Complete <strong>Organisation Details</strong> (step 1) first. The organisation must exist before you can create an account.
          </Alert>
        </StepPanel>
        <WizardFooter stepId={currentStep} />
      </>
    );
  }

  return (
    <>
      <StepPanel stepId={2} stepStatus={steps.account}>
        <Stack spacing={3.5} divider={<Divider flexItem />}>
          <FormSection
            icon={<BusinessIcon fontSize="small" />}
            title="Account Information"
            description="Create your first account for this organisation"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Field
                  label="Account Name"
                  placeholder="e.g., Main Account, Branch Office"
                  value={account.name}
                  onChange={(e) => updateAccount('name', e.target.value)}
                  error={!!stepErrors.account_name}
                  helperText={stepErrors.account_name}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Comments/Notes"
                  placeholder="Add any additional notes about this account"
                  value={account.comments}
                  onChange={(e) => updateAccount('comments', e.target.value)}
                  error={!!stepErrors.account_comments}
                  helperText={stepErrors.account_comments}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<PhoneIcon fontSize="small" />}
            title="Contact Information"
            description="Account contact details"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Phone Number"
                  placeholder="e.g., 9876543210"
                  value={account.phone || ''}
                  onChange={(e) => updateAccount('phone', e.target.value)}
                  error={!!stepErrors.phone}
                  helperText={stepErrors.phone}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Email Address"
                  placeholder="e.g., account@company.com"
                  type="email"
                  value={account.email || ''}
                  onChange={(e) => updateAccount('email', e.target.value)}
                  error={!!stepErrors.email}
                  helperText={stepErrors.email}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<LocationIcon fontSize="small" />}
            title="Address & Location"
            description="Account location details"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Field
                  label="Address"
                  placeholder="e.g., 123 Business Street, Suite 100"
                  value={account.address || ''}
                  onChange={(e) => updateAccount('address', e.target.value)}
                  error={!!stepErrors.address}
                  helperText={stepErrors.address}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<PersonIcon fontSize="small" />}
            title="Reference Person"
            description="Primary reference contact for this account"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Reference Name"
                  placeholder="Full name"
                  value={account.reference_name || ''}
                  onChange={(e) => updateAccount('reference_name', e.target.value)}
                  error={!!stepErrors.reference_name}
                  helperText={stepErrors.reference_name}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Reference Phone"
                  placeholder="e.g., 9876543210"
                  value={account.reference_phone || ''}
                  onChange={(e) => updateAccount('reference_phone', e.target.value)}
                  error={!!stepErrors.reference_phone}
                  helperText={stepErrors.reference_phone}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Reference Email"
                  placeholder="e.g., reference@company.com"
                  type="email"
                  value={account.reference_email || ''}
                  onChange={(e) => updateAccount('reference_email', e.target.value)}
                  error={!!stepErrors.reference_email}
                  helperText={stepErrors.reference_email}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={sx.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<PersonIcon fontSize="small" />}
            title="Account Contacts"
            description="Add key contacts for this account"
          >
            {account.contacts.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No contacts added yet. Click the button below to add your first contact.
              </Alert>
            ) : (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {account.contacts.map((contact, index) => (
                  <Box
                    key={contact.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Contact {index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Contact Name"
                            placeholder="Full name"
                            value={contact.name}
                            onChange={(e) => updateContact(index, 'name', e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon sx={{ fontSize: 18, color: 'action.active' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Email"
                            placeholder="contact@example.com"
                            type="email"
                            value={contact.email}
                            onChange={(e) => updateContact(index, 'email', e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon sx={{ fontSize: 18, color: 'action.active' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Phone Number"
                            placeholder="+1 (555) 000-0000"
                            value={contact.phone}
                            onChange={(e) => updateContact(index, 'phone', e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon sx={{ fontSize: 18, color: 'action.active' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Designation"
                            placeholder="e.g., Manager, Coordinator"
                            value={contact.designation || ''}
                            onChange={(e) => updateContact(index, 'designation', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Department"
                            placeholder="e.g., Sales, Operations"
                            value={contact.department || ''}
                            onChange={(e) => updateContact(index, 'department', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={contact.singlePointOfContact === 'yes' || contact.singlePointOfContact === true}
                                onChange={(e) => updateContact(index, 'singlePointOfContact', e.target.checked ? 'yes' : 'no')}
                              />
                            }
                            label="Primary Contact"
                          />
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => removeContact(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addContact}
              size="small"
              fullWidth
            >
              Add Contact
            </Button>
          </FormSection>

          <FormSection
            icon={<BusinessIcon fontSize="small" />}
            title="Account Summary"
            description="Review your account details"
          >
            <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Organisation:</strong> {draft.organisation.name || createdOrgId}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Account Name:</strong> {account.name || '(Not set)'}
              </Typography>
              {account.phone && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Phone:</strong> {account.phone}
                </Typography>
              )}
              {account.email && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Email:</strong> {account.email}
                </Typography>
              )}
              {account.address && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Address:</strong> {account.address}
                </Typography>
              )}
              {account.reference_name && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Reference:</strong> {account.reference_name}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Contacts:</strong> {account.contacts.length} contact(s)
              </Typography>
              {account.comments && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Notes:</strong> {account.comments}
                </Typography>
              )}
            </Box>
          </FormSection>
        </Stack>
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
